import React from 'react';
import { Leaf, ShieldCheck, Wrench, ArrowRight } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

export const PainPointsSection: React.FC = () => {
  const esgFeatures = [
    {
      title: '보증된 협력사 연계',
      desc: '1등급 전문 협력사를 통해 시공하여 폐기물을 최소화하고 설비의 수명을 극대화합니다.',
      icon: ShieldCheck,
    },
    {
      title: '고효율 친환경 패널',
      desc: '탄소 배출이 적은 공정으로 생산된 고효율 친환경 패널을 적용합니다.',
      icon: Leaf,
    },
    {
      title: '완벽한 사후 관리',
      desc: '정기적인 유지보수와 모니터링을 통해 발전 효율을 유지하고 자원 낭비를 방지합니다.',
      icon: Wrench,
    },
  ];

  const scrollToConsultation = () => {
    const elem = document.getElementById('consultation');
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pain-points" className="py-20 sm:py-28 bg-white text-slate-900 relative border-t border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* === Section 3 Header: 우리가 함께 만든 내일의 변화 === */}
        <ScrollReveal direction="up">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight mb-4 leading-snug keep-all">
              "우리가 함께 만든 <span className="text-sky-600 underline decoration-sky-300 underline-offset-8">내일의 변화</span>"
            </h2>
            
            <p className="text-lg sm:text-xl font-bold text-slate-700 keep-all mb-2">
              화석 연료를 대체하여 생산한 청정에너지의 총량
            </p>
            <p className="text-sm sm:text-base font-extrabold text-sky-600 keep-all">
              성공적인 ESG 경영, 플러스에스디가 함께합니다.
            </p>
          </div>
        </ScrollReveal>

        {/* Featured ESG Image & Content Showcase Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center mb-12 sm:mb-16">
          
          {/* Left Column: User Provided Image */}
          <div className="lg:col-span-6">
            <ScrollReveal direction="right">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl border border-slate-200 group">
                <img
                  src="https://drive.google.com/thumbnail?id=1Xedw3EfChggu15jPmqViSwv1UO1cawFz&sz=w1200"
                  alt="우리가 함께 만든 내일의 변화 - 플러스에스디 ESG 태양광"
                  className="w-full h-[240px] sm:h-[420px] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.src = "https://lh3.googleusercontent.com/d/1Xedw3EfChggu15jPmqViSwv1UO1cawFz";
                  }}
                />

                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent p-4 sm:p-6 text-white">
                  <p className="text-[10px] sm:text-xs text-sky-300 font-extrabold tracking-wider uppercase mb-0.5">
                    SUSTAINABLE FUTURE
                  </p>
                  <p className="text-sm sm:text-lg font-black leading-snug">
                    지구 환경 보호와 안정적인 수익 창출의 완벽한 조화
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column: 3 ESG Feature Cards */}
          <div className="lg:col-span-6 space-y-3 sm:space-y-4">
            {esgFeatures.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <ScrollReveal key={idx} direction="left" delay={idx * 0.15}>
                  <div className="bg-slate-50 hover:bg-sky-50/60 rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-slate-200/80 hover:border-sky-300 transition-all duration-300 shadow-sm flex items-start sm:items-center gap-3.5 sm:gap-4 group">
                    <div className="p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-white border border-slate-200 text-sky-600 shadow-sm group-hover:scale-105 group-hover:bg-sky-500 group-hover:text-white transition-all shrink-0">
                      <IconComp className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    
                    <div className="space-y-0.5 sm:space-y-1">
                      <h3 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed keep-all font-medium">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

        </div>

        {/* Bottom Banner Callout */}
        <ScrollReveal direction="up">
          <div className="relative rounded-2xl sm:rounded-3xl bg-gradient-to-r from-slate-900 via-sky-950 to-slate-900 p-6 sm:p-10 text-white shadow-xl overflow-hidden border border-slate-800 text-center">
            {/* Background Glow */}
            <div className="absolute -right-16 -top-16 w-48 h-48 sm:w-64 sm:h-64 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -left-16 -bottom-16 w-48 h-48 sm:w-64 sm:h-64 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center justify-center space-y-4 sm:space-y-6">
              <h3 className="text-xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight keep-all drop-shadow">
                "지금 바로 <span className="text-sky-400">지구와 대표님</span>을 위한 <br className="hidden sm:inline" />
                가치 있는 선택을 하세요."
              </h3>

              <p className="text-slate-300 text-xs sm:text-base font-medium max-w-2xl keep-all">
                유휴 지붕과 방치 부지가 미래형 ESG 임대 자산으로 거듭납니다. 초기 자본 없이 안전하게 시작하십시오.
              </p>

              <button
                onClick={scrollToConsultation}
                className="w-full sm:w-auto justify-center mt-2 px-7 py-3.5 sm:py-4 rounded-xl sm:rounded-2xl bg-sky-500 hover:bg-sky-400 text-white text-xs sm:text-base font-black transition-all shadow-xl shadow-sky-500/30 active:scale-95 cursor-pointer flex items-center gap-2 group"
              >
                <span>지금 가치 있는 선택 신청하기</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </ScrollReveal>

      </div>
    </section>
  );
};
